/**
 * LinkNumbersModal - "Este numero e a mesma pessoa".
 *
 * Um contato (lead) pode ter N numeros de WhatsApp. Aqui o operador ve os
 * numeros ja vinculados a esta pessoa e vincula um novo. Espelha a linguagem
 * visual do TransferContactModal (overlay, container, header/body/footer).
 *
 * Regra de duplicado (decisao de produto): ao vincular um numero que JA pertence
 * a outro lead, BLOQUEIA e avisa (sem merge nesta rodada) — checa via /search
 * antes de /link. Remover um numero mantem sempre >=1 (via /unlink).
 *
 * Gated por useFeatureFlags.lead_model no caller (nao renderiza a entrada com a
 * flag off).
 */
import React, { useEffect, useState } from 'react';
import { X, Users, Plus, Loader2, AlertTriangle, Trash2 } from 'lucide-react';
import leadsService from '../../services/global/leadsService';
import { formatPhoneNumber } from '../../utils/wa-personal/chat_utils';

const onlyDigits = (v) => String(v || '').replace(/\D/g, '');

const LinkNumbersModal = ({
  isOpen,
  onClose,
  contactPhone,
  contactName,
  colors,
  channel = 'wa_personal',
  zIndex = 1000,
  onChanged,            // (identities) => void  — apos vincular/desvincular
}) => {
  const [leadId, setLeadId] = useState(null);
  const [identities, setIdentities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [linking, setLinking] = useState(false);
  const [removingRef, setRemovingRef] = useState(null);
  const [newNumber, setNewNumber] = useState('');
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null); // aviso de duplicado (nao e erro)

  useEffect(() => {
    if (!isOpen || !contactPhone) return undefined;
    let cancelled = false;
    setLeadId(null);
    setIdentities([]);
    setNewNumber('');
    setError(null);
    setNotice(null);
    setLinking(false);
    setRemovingRef(null);
    setLoading(true);

    // Resolve o lead DESTE contato (cria se ainda nao existir — e a pessoa a
    // quem vamos anexar outro numero).
    leadsService
      .resolve(channel, contactPhone)
      .then((res) => {
        if (cancelled) return;
        setLeadId(res?.data?.lead_id || null);
        setIdentities(res?.data?.identities || []);
      })
      .catch((err) => {
        if (!cancelled) {
          setError('Não foi possível carregar os números deste contato.');
          console.error(err);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, contactPhone, channel]);

  const refresh = async () => {
    if (!leadId) return;
    const res = await leadsService.get(leadId);
    const ids = res?.data?.identities || [];
    setIdentities(ids);
    if (typeof onChanged === 'function') onChanged(ids);
  };

  const handleLink = async () => {
    if (linking || !leadId) return;
    const digits = onlyDigits(newNumber);
    setError(null);
    setNotice(null);
    if (digits.length < 8) {
      setError('Informe um número válido (com DDI/DDD para não-BR).');
      return;
    }
    // Ja e um dos numeros deste contato?
    if (identities.some((i) => onlyDigits(i.external_id) === digits)) {
      setNotice('Este número já está vinculado a este contato.');
      return;
    }
    setLinking(true);
    try {
      // Decisao: BLOQUEAR em duplicado — o numero ja pertence a outro lead?
      const found = await leadsService.search(digits, channel);
      if (found?.data?.found && found.data.lead_id && found.data.lead_id !== leadId) {
        const who = found.data.display_name || 'outro contato';
        setNotice(`Este número já pertence a ${who}. Desvincule-o de lá primeiro.`);
        setLinking(false);
        return;
      }
      await leadsService.link(leadId, channel, digits);
      setNewNumber('');
      await refresh();
    } catch (err) {
      if (err?.response?.status === 409) {
        setNotice('Este número já pertence a outro contato.');
      } else {
        setError('Erro ao vincular o número. Tente novamente.');
        console.error(err);
      }
    } finally {
      setLinking(false);
    }
  };

  const handleUnlink = async (identity) => {
    if (removingRef || identities.length <= 1) return;
    setRemovingRef(identity.ref);
    setError(null);
    setNotice(null);
    try {
      await leadsService.unlink(leadId, identity.channel || channel, identity.external_id);
      await refresh();
    } catch (err) {
      setError('Erro ao desvincular o número.');
      console.error(err);
    } finally {
      setRemovingRef(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)', zIndex,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '20px',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: colors.bgPrimary,
          borderRadius: '16px',
          width: '100%', maxWidth: '440px',
          overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          maxHeight: '85vh',
          display: 'flex', flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 20px', borderBottom: `1px solid ${colors.borderColor}`,
          flexShrink: 0,
        }}>
          <div>
            <h3 style={{
              margin: 0, fontSize: '16px', fontWeight: 600, color: colors.textPrimary,
              display: 'flex', alignItems: 'center', gap: '8px',
            }}>
              <Users size={18} color={colors.accentPrimary} /> Números do contato
            </h3>
            <p style={{ margin: '2px 0 0', fontSize: '12px', color: colors.textSecondary }}>
              {contactName || formatPhoneNumber(contactPhone) || contactPhone}
            </p>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer', color: colors.textSecondary,
            padding: '8px', borderRadius: '8px',
          }}>
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div style={{
          padding: '16px 20px', display: 'flex', flexDirection: 'column',
          gap: '14px', overflowY: 'auto', flex: 1,
        }}>
          {loading ? (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: '24px 0', color: colors.textTertiary,
            }}>
              <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
            </div>
          ) : (
            <>
              {/* Numeros ja vinculados */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <span style={{ fontSize: '12px', fontWeight: 500, color: colors.textSecondary }}>
                  {identities.length > 1
                    ? `${identities.length} números desta pessoa`
                    : 'Número deste contato'}
                </span>
                {identities.map((i) => (
                  <div
                    key={i.ref}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '8px 12px', borderRadius: '8px',
                      backgroundColor: colors.bgSecondary,
                    }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '14px', color: colors.textPrimary }}>
                        {formatPhoneNumber(i.external_id) || i.external_id}
                      </span>
                      {i.primary && (
                        <span style={{
                          fontSize: '10px', fontWeight: 700, color: colors.accentPrimary,
                          textTransform: 'uppercase', letterSpacing: '0.4px',
                        }}>
                          principal
                        </span>
                      )}
                    </span>
                    {identities.length > 1 && (
                      <button
                        onClick={() => handleUnlink(i)}
                        disabled={!!removingRef}
                        title="Desvincular número"
                        style={{
                          background: 'none', border: 'none', cursor: removingRef ? 'wait' : 'pointer',
                          color: colors.textTertiary, padding: '4px', borderRadius: '6px',
                          display: 'inline-flex',
                        }}
                      >
                        {removingRef === i.ref ? (
                          <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                        ) : (
                          <Trash2 size={14} />
                        )}
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {/* Vincular novo numero */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <span style={{ fontSize: '12px', fontWeight: 500, color: colors.textSecondary }}>
                  Vincular outro número (mesma pessoa)
                </span>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="tel"
                    value={newNumber}
                    onChange={(e) => { setNewNumber(e.target.value); setNotice(null); setError(null); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleLink(); }}
                    placeholder="Ex.: 5511999999999"
                    style={{
                      flex: 1, padding: '10px 12px', borderRadius: '8px',
                      border: `1px solid ${colors.borderColor}`,
                      background: colors.bgSecondary, fontSize: '13px',
                      color: colors.textPrimary, outline: 'none',
                    }}
                  />
                  <button
                    onClick={handleLink}
                    disabled={linking}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: '6px',
                      padding: '0 14px', backgroundColor: colors.accentPrimary, color: 'white',
                      border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 600,
                      cursor: linking ? 'wait' : 'pointer', opacity: linking ? 0.7 : 1,
                    }}
                  >
                    {linking ? (
                      <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                    ) : (
                      <Plus size={14} />
                    )}
                    Vincular
                  </button>
                </div>
              </div>

              {notice && (
                <div style={{
                  display: 'flex', alignItems: 'flex-start', gap: '8px',
                  padding: '10px 12px', borderRadius: '8px',
                  backgroundColor: 'rgba(245,158,11,0.10)',
                  border: '1px solid rgba(245,158,11,0.4)',
                }}>
                  <AlertTriangle size={16} color="#b45309" style={{ flexShrink: 0, marginTop: 1 }} />
                  <span style={{ fontSize: '12.5px', lineHeight: '18px', color: colors.textPrimary }}>
                    {notice}
                  </span>
                </div>
              )}
              {error && (
                <span style={{ fontSize: '12px', color: '#ef4444' }}>{error}</span>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex', justifyContent: 'flex-end', alignItems: 'center',
          padding: '12px 20px', borderTop: `1px solid ${colors.borderColor}`,
          flexShrink: 0,
        }}>
          <button
            onClick={onClose}
            style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: '8px 18px', backgroundColor: colors.accentPrimary, color: 'white',
              border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Concluído
          </button>
        </div>
      </div>
    </div>
  );
};

export default LinkNumbersModal;
